<template>
    <div class="bg-white shadow rounded-2xl p-6">
        <h2 class="text-xl font-semibold mb-4">
            {{ reviewTitle }}
        </h2>

        <form @submit.prevent="submitReview" class="space-y-4">

            <!-- ESTRELAS -->
            <div>
                <label class="block font-medium mb-2">Nota</label>

                <div class="flex gap-2">
                    <button
                        v-for="star in 5"
                        :key="star"
                        type="button"
                        @click="rating = star"
                        class="text-2xl"
                        :class="star <= rating ? 'text-yellow-400' : 'text-gray-300'"
                    >
                        ★
                    </button>
                </div>
            </div>

            <!-- COMENTÁRIO -->
            <div>
                <label class="block font-medium mb-2">Comentário</label>
                <textarea
                    v-model="comment"
                    rows="4"
                    class="input-gold"
                    :placeholder="commentPlaceholder"
                />
            </div>
            <p v-if="props.expiresAt" class="text-sm text-gray-500 mt-2">
                Avaliação disponível até
                <strong>{{ formatDate(props.expiresAt) }}</strong>
            </p>

            <!-- BOTÃO -->
            <div class="flex justify-end">
                <button
                    type="submit"
                    :disabled="loading"
                    class="bg-[#b99a52] hover:bg-[#a18446] text-white px-6 py-3 rounded-xl font-semibold shadow"
                >
                    <span v-if="!loading">Enviar avaliação</span>
                    <span v-else>Enviando...</span>
                </button>
            </div>

        </form>
    </div>
</template>

<script setup>
import { ref, getCurrentInstance, computed } from 'vue'
import api from '@/services/api'

const props = defineProps({
    projectId: Number,
    reviewedId: Number, // contratante
    reviewerId: Number,
    reviewType: {
        type: String,
        default: 'contractor', // 'contractor' | 'freelancer'
    },
    expiresAt: {
        type: [String, Date],
        default: null
    }
})

const { proxy } = getCurrentInstance()

const rating = ref(0)
const comment = ref('')
const loading = ref(false)

const reviewTitle = computed(() => {
    return props.reviewType === 'freelancer'
        ? 'Avaliar freelancer'
        : 'Avaliar contratante'
})

const isExpired = computed(() => {
    if (!props.expiresAt) return false
    return new Date(props.expiresAt).getTime() <= Date.now()
})

const commentPlaceholder = computed(() => {
    return props.reviewType === 'freelancer'
        ? 'Conte como foi sua experiência com o freelancer...'
        : 'Conte como foi sua experiência com o contratante...'
})

async function submitReview() {
    if (isExpired.value) {
        proxy.$swal.fire({
            icon: 'warning',
            title: 'Prazo encerrado',
            text: 'O prazo para enviar esta avaliação expirou.',
            customClass: {
                container: 'swal-top-layer'
            }
        })
        return
    }

    if (rating.value === 0) {
        proxy.$swal.fire({
            icon: 'warning',
            title: 'Avaliação obrigatória',
            text: 'Selecione uma nota de 1 a 5.',
            customClass: {
                container: 'swal-top-layer'
            }
        })
        return
    }

    try {
        loading.value = true

        await api.post('/reviews', {
            project_id: props.projectId,
            reviewed_id: props.reviewedId,
            reviewer_id: props.reviewerId,
            rating: rating.value,
            comment: comment.value
        })

        proxy.$swal.fire({
            icon: 'success',
            title: '',
            text: 'Avaliação enviada com sucesso!',
            customClass: {
                container: 'swal-top-layer'
            }
        })

        rating.value = 0
        comment.value = ''

        await new Promise(resolve => setTimeout(resolve, 3000));
        
        window.location.reload();

    } catch (error) {
        proxy.$swal.fire({
            icon: 'error',
            title: 'Erro',
            text: error?.response?.data?.error || 'Erro ao enviar avaliação.',
            customClass: {
                container: 'swal-top-layer'
            }
        })
    } finally {
        loading.value = false
    }
}

function formatDate(dt) {
    if (!dt) return "-";
    return new Date(dt).toLocaleDateString("pt-BR");
}
</script>


<style>
.swal-top-layer {
    z-index: 99999 !important;
}
</style>